/**
 * Arborescence des espaces (04/11/2026, demande du dirigeant : « faire une arborescence avec les dossiers, que ce soit
 * intuitif »). Fonctions pures qui transforment les données EXISTANTES d'un dossier (pièces, questionnaires, supports)
 * en un arbre à afficher ; aucun nouveau statut : chaque état se déduit des statuts déjà en base.
 *
 *  - apprenant : Formation › Avant / Pendant / Après › documents et formulaires ;
 *  - formateur : Dossier › Pièces communes + un nœud par apprenant › ses pièces.
 *
 * Phases (hypothèse H-135) : AVT → « Avant la formation » ; PDT → « Pendant la formation » ; FIN et APR → « Après la
 * formation » (évaluation des acquis, attestation, satisfaction, factures).
 */
import type { Phase } from "../referentiel/pieces";

/**
 * - `fait` : validé (signé, déposé, renseigné) ou transmis ;
 * - `a_faire` : attend une action de la personne qui regarde ;
 * - `en_attente` : quelqu'un d'autre a la main (retour attendu, validation de l'organisme) ;
 * - `a_telecharger` : document transmis, à lire ou télécharger ;
 * - `a_venir` : pas encore ouvert.
 */
export type EtatNoeud = "fait" | "a_faire" | "en_attente" | "a_telecharger" | "a_venir";

export const LIBELLES_ETAT: Record<EtatNoeud, string> = {
  fait: "Fait",
  a_faire: "À faire",
  en_attente: "En attente",
  a_telecharger: "À télécharger",
  a_venir: "À venir",
};

export interface NoeudArbre {
  id: string;
  libelle: string;
  /** Genre du nœud, pour l'icône : formation, étape, document, formulaire, supports, dossier, apprenant, groupe. */
  genre: "formation" | "etape" | "document" | "formulaire" | "supports" | "dossier" | "apprenant" | "groupe";
  etat: EtatNoeud | null;
  /** Précision courte (« 2 à faire », « Validé »…). */
  detail?: string;
  /** Où mène le nœud : une ancre de la page (`#…`) ou une adresse de l'application (`/dossiers/…`). */
  cible?: string;
  enfants: NoeudArbre[];
}

export type GroupePhase = "avant" | "pendant" | "apres";
export const GROUPES_PHASE: ReadonlyArray<{ cle: GroupePhase; libelle: string }> = [
  { cle: "avant", libelle: "Avant la formation" },
  { cle: "pendant", libelle: "Pendant la formation" },
  { cle: "apres", libelle: "Après la formation" },
];
export function groupeDePhase(phase: Phase): GroupePhase {
  return phase === "AVT" ? "avant" : phase === "PDT" ? "pendant" : "apres";
}

/** Pièce telle que l'API du dossier la décrit (sous-ensemble). */
export interface PieceArbre {
  id: string;
  code: string;
  libelle: string;
  phase: Phase;
  stagiaire_id: string | null;
  suivi: boolean;
  statut: "en_attente" | "valide" | null;
  disponible: boolean;
  peut_signer: boolean;
  peut_deposer: boolean;
  transmise_le?: string | Date | null;
}

export interface QuestionnaireArbre {
  type: "recueil" | "positionnement" | "acquis" | "satisfaction_chaud" | "satisfaction_froid";
  ouvert: boolean;
  valide: boolean;
}

const QUESTIONNAIRES: Record<QuestionnaireArbre["type"], { libelle: string; phase: Phase; section: string }> =
  {
    recueil: { libelle: "Recueil des besoins", phase: "AVT", section: "preliminaire" },
    positionnement: { libelle: "Test de positionnement", phase: "AVT", section: "preliminaire" },
    acquis: { libelle: "Évaluation des acquis", phase: "FIN", section: "realisation" },
    satisfaction_chaud: { libelle: "Satisfaction à chaud", phase: "FIN", section: "realisation" },
    satisfaction_froid: { libelle: "Satisfaction à froid", phase: "APR", section: "realisation" },
  };

/** État d'une pièce vue par l'apprenant. */
export function etatPieceApprenant(p: PieceArbre): EtatNoeud {
  if (!p.suivi) return p.disponible ? "a_telecharger" : "a_venir";
  if (p.statut === "valide") return "fait";
  if (p.peut_signer || p.peut_deposer) return "a_faire";
  return "en_attente";
}

/** État d'une pièce vue par le formateur : il agit sur l'ordre de mission et l'engagement du bilan ; le reste attend un retour. */
export function etatPieceFormateur(
  p: Pick<PieceArbre, "code" | "suivi" | "statut" | "transmise_le">,
): EtatNoeud {
  if (!p.suivi) return p.transmise_le ? "fait" : "en_attente";
  if (p.statut === "valide") return "fait";
  return p.code === "04-AVT" || p.code === "ECB" ? "a_faire" : "en_attente";
}

/** État d'un nœud parent : « à faire » s'il reste une action, « fait » si tout est fait, « à venir » si rien n'est ouvert. */
export function etatAgrege(etats: readonly (EtatNoeud | null)[]): EtatNoeud | null {
  const e = etats.filter((x): x is EtatNoeud => x !== null);
  if (e.length === 0) return null;
  if (e.includes("a_faire")) return "a_faire";
  if (e.every((x) => x === "fait" || x === "a_telecharger")) return "fait";
  if (e.every((x) => x === "a_venir")) return "a_venir";
  return "en_attente";
}

function detailDe(enfants: readonly NoeudArbre[]): string | undefined {
  const n = enfants.filter((x) => x.etat === "a_faire").length;
  if (n > 0) return n === 1 ? "1 à faire" : `${n} à faire`;
  const faits = enfants.filter((x) => x.etat === "fait" || x.etat === "a_telecharger").length;
  return enfants.length > 0 ? `${faits}/${enfants.length}` : undefined;
}

/** Pièce → section de la page apprenant où elle s'affiche (pour faire défiler jusqu'à elle). */
const SECTION_DE_PIECE: Record<string, string> = {
  PRE: "financement",
  "02-AVT": "financement",
  "03-AVT": "financement",
  PRG: "financement",
  MAN: "financement",
  ACC: "accord",
  "05-AVT": "realisation",
  "06-PDT": "realisation",
  "07-FIN": "realisation",
  "09-FIN": "realisation",
};

export interface EntreeArbreApprenant {
  dossier_id: string;
  formation_titre: string;
  pieces: readonly PieceArbre[];
  questionnaires: readonly QuestionnaireArbre[];
  /** Nombre de supports partagés ; null = coffre encore fermé. */
  supports: number | null;
  /** Sections affichées sur la page (les ancres n'existent que pour elles). */
  sections: readonly string[];
}

export function arbreApprenant(e: EntreeArbreApprenant): NoeudArbre {
  const ancre = (section: string | undefined) =>
    section && e.sections.includes(section) ? `#section-${section}` : undefined;
  const feuilles: Array<NoeudArbre & { groupe: GroupePhase }> = [
    ...e.questionnaires.map((q) => {
      const def = QUESTIONNAIRES[q.type];
      const etat: EtatNoeud = q.valide ? "fait" : q.ouvert ? "a_faire" : "a_venir";
      return {
        id: `q-${q.type}`,
        libelle: def.libelle,
        genre: "formulaire" as const,
        etat,
        detail: LIBELLES_ETAT[etat],
        cible: ancre(def.section),
        enfants: [],
        groupe: groupeDePhase(def.phase),
      };
    }),
    ...e.pieces.map((p) => {
      const etat = etatPieceApprenant(p);
      return {
        id: `p-${p.id}`,
        libelle: p.libelle,
        genre: "document" as const,
        etat,
        detail: LIBELLES_ETAT[etat],
        cible: ancre(SECTION_DE_PIECE[p.code]),
        enfants: [],
        groupe: groupeDePhase(p.phase),
      };
    }),
  ];
  const etapes: NoeudArbre[] = GROUPES_PHASE.map(({ cle, libelle }) => {
    const enfants = feuilles.filter((f) => f.groupe === cle).map(({ groupe: _g, ...n }) => n);
    return {
      id: `etape-${cle}`,
      libelle,
      genre: "etape" as const,
      etat: etatAgrege(enfants.map((x) => x.etat)),
      detail: detailDe(enfants),
      enfants,
    };
  }).filter((n) => n.enfants.length > 0);
  if (e.supports !== null || etapes.length > 0) {
    const etat: EtatNoeud = e.supports === null ? "a_venir" : e.supports > 0 ? "a_telecharger" : "en_attente";
    etapes.push({
      id: "supports",
      libelle: "Supports de cours",
      genre: "supports",
      etat,
      detail:
        e.supports === null
          ? "Après l'accord"
          : e.supports === 0
            ? "Bientôt"
            : `${e.supports} fichier${e.supports > 1 ? "s" : ""}`,
      cible: "#supports",
      enfants: [],
    });
  }
  return {
    id: `formation-${e.dossier_id}`,
    libelle: e.formation_titre,
    genre: "formation",
    etat: etatAgrege(etapes.map((x) => x.etat)),
    // À la racine, on compte les ACTIONS restantes (feuilles), pas les étapes.
    detail: detailDe(etapes.flatMap((x) => (x.enfants.length > 0 ? x.enfants : [x]))),
    enfants: etapes,
  };
}

export interface DossierArbre {
  id: string;
  reference: string;
  titre: string;
  libelle_statut: string;
  archive: boolean;
  stagiaires: ReadonlyArray<{ id: string; nom: string }>;
  pieces: ReadonlyArray<
    Pick<
      PieceArbre,
      "id" | "code" | "libelle" | "phase" | "stagiaire_id" | "suivi" | "statut" | "transmise_le"
    >
  >;
}

/** Arborescence du formateur : Dossier › Pièces communes / Apprenant › pièces, triée par phase. */
export function arbreFormateur(dossiers: readonly DossierArbre[]): NoeudArbre[] {
  const ordrePhase: Record<Phase, number> = { AVT: 0, PDT: 1, FIN: 2, APR: 3 };
  const feuille = (d: DossierArbre, p: DossierArbre["pieces"][number]): NoeudArbre => {
    const etat = etatPieceFormateur(p);
    return {
      id: `${d.id}-p-${p.id}`,
      libelle: p.libelle,
      genre: "document",
      etat,
      detail: !p.suivi && p.transmise_le ? "Transmis" : !p.suivi ? "À transmettre" : LIBELLES_ETAT[etat],
      cible: `/dossiers/${d.id}`,
      enfants: [],
    };
  };
  const tri = <T extends { phase: Phase }>(l: readonly T[]) =>
    [...l].sort((a, b) => ordrePhase[a.phase] - ordrePhase[b.phase]);
  return dossiers.map((d) => {
    const communes = tri(d.pieces.filter((p) => p.stagiaire_id === null)).map((p) => feuille(d, p));
    const parApprenant: NoeudArbre[] = d.stagiaires.map((st) => {
      const enfants = tri(d.pieces.filter((p) => p.stagiaire_id === st.id)).map((p) => feuille(d, p));
      return {
        id: `${d.id}-a-${st.id}`,
        libelle: st.nom,
        genre: "apprenant",
        etat: etatAgrege(enfants.map((x) => x.etat)),
        detail: detailDe(enfants),
        cible: `/dossiers/${d.id}`,
        enfants,
      };
    });
    const enfants: NoeudArbre[] = [
      ...(communes.length > 0
        ? [
            {
              id: `${d.id}-communes`,
              libelle: "Pièces du dossier",
              genre: "groupe" as const,
              etat: etatAgrege(communes.map((x) => x.etat)),
              detail: detailDe(communes),
              cible: `/dossiers/${d.id}`,
              enfants: communes,
            },
          ]
        : []),
      ...parApprenant,
    ];
    return {
      id: `d-${d.id}`,
      libelle: `${d.reference} · ${d.titre}`,
      genre: "dossier",
      etat: d.archive ? "fait" : etatAgrege(enfants.map((x) => x.etat)),
      detail: d.libelle_statut,
      cible: `/dossiers/${d.id}`,
      enfants,
    };
  });
}
